import React, {useEffect, useState, useRef} from 'react';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import {FixedSizeList} from 'react-window';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import {fade, lighten, makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import InputBase from '@material-ui/core/InputBase';
import JString from '@easytech-international-sdn-bhd/jstring';
import {empty} from '../../Helper/Constants';
import { blue } from '@material-ui/core/colors';
import Divider from '@material-ui/core/Divider';

const useToolbarStyles = makeStyles(theme => ({
	root: {
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(1),
	},
    dialogTitle: {
        paddingBottom: theme.spacing(1),
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
	viewInRow: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
        marginTop: theme.spacing(2),
	},
}));

const useSearchStyle = makeStyles((theme) => ({
	margin: {
		// marginTop: theme.spacing(1) // Removed to let list handle spacing
	},
	root: {
		flexGrow: 1,
	},
	search: {
		position: 'relative',
		borderRadius: 16, // Modern rounded corners
		backgroundColor: fade(theme.palette.common.black, 0.04),
		border: `1px solid ${fade(theme.palette.common.black, 0.08)}`,
		marginLeft: 0,
		width: '100%',
        transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color', 'transform'], {
            duration: theme.transitions.duration.short,
        }),
		'&:hover': {
			backgroundColor: fade(theme.palette.common.black, 0.06),
            borderColor: fade(theme.palette.common.black, 0.2),
		},
        '&:focus-within': {
            backgroundColor: fade(theme.palette.common.black, 0.02),
            borderColor: theme.palette.text.primary, // Dark border for consistency
            boxShadow: `0 4px 12px ${fade(theme.palette.common.black, 0.08)}`, // Soft shadow
            transform: 'translateY(-1px)', // Subtle lift
        },
	},
	searchIcon: {
		padding: theme.spacing(0, 2),
		height: '100%',
		position: 'absolute',
		pointerEvents: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
        color: theme.palette.text.secondary,
        transition: theme.transitions.create('color'),
        '$search:focus-within &': {
            color: theme.palette.text.primary,
        },
	},
	inputRoot: {
		color: theme.palette.text.primary,
		width: '100%',
        fontWeight: 500,
	},
	inputInput: {
		padding: theme.spacing(1.5, 1, 1.5, 0),
		paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
		width: '100%',
        fontSize: '0.95rem',
	},
    highlight: {
        color: theme.palette.primary.main,
        fontWeight: 700,
    },
    listItem: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-selected': {
            backgroundColor: fade(theme.palette.primary.main, 0.08),
            '&:hover': {
                backgroundColor: fade(theme.palette.primary.main, 0.12),
            },
            borderLeft: `4px solid ${theme.palette.primary.main}`, // Visual indicator for selection
        }
    },
    // Custom scrollbar styles
    scrollContainer: {
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent', // Transparent track
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.text.secondary, // Use theme text secondary color for scrollbar
            borderRadius: '4px',
            opacity: 0.5,
            '&:hover': {
                backgroundColor: theme.palette.text.primary, // Darker on hover
            },
        },
        // Firefox scrollbar
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.palette.text.secondary} transparent`,
    }
}));

export default function VirtualListMB({
										setClose,
                                        onConfirm,
                                        onCreate,
                                        onCancel,
										open,
										title,
                                        description,
										second_description,
										data,
										language,
										target = '',
										tag = '',
										cancel = '',
										hideValue = false
									}) {
	const [custList, setCustList] = useState(data);
	const [selected, setSelected] = useState(empty);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [keywords, setKeywords] = useState([]);
	const classes = useToolbarStyles();
	const searchClass = useSearchStyle();
    const listRef = useRef(null);

	useEffect(() => {
		setCustList(custList => (data));
	}, [data]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTo(0);
        }
    }, [custList]);

	const highlightText = (str) => {
		if (!str) { return str; }
		const safe = (keywords || []).filter(k => k).map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
		if (safe.length === 0) { return str; }
		const re = new RegExp('(' + safe.join('|') + ')', 'gi');
		const kwSet = new Set((keywords || []).filter(k => k));
		return String(str).split(re).map((part, i) => (
			part && kwSet.has(part.toLowerCase())
				? <span key={i} className={searchClass.highlight}>{part}</span>
				: part
		));
	}

	const renderRow = (props) => {
		const {index, style} = props;
		const _cur = custList[index];
		if (index !== custList.length) {
			let strvalue = '';
			if (target == 'true' && tag == 'true') {
				for (let i = 0; i < _cur.value.length; i++) {
					if (i == _cur.value.length - 1) {
						strvalue = strvalue + _cur.value[i];
					} else {
						strvalue = strvalue + _cur.value[i] + ', ';
					}
				}
			}
			return (
				<ListItem 
                    button 
                    selected={selectedIndex == index} 
                    style={style} 
                    key={index} 
                    className={searchClass.listItem} 
                    onClick={() => {
                        setSelected(_cur);
                        setSelectedIndex(index);
                    }}
                >
					<ListItemText
						primary={
                            <Typography variant="subtitle1" color={selectedIndex == index ? "primary" : "textPrimary"} noWrap style={{ fontWeight: selectedIndex == index ? 600 : 500 }}>
                                {highlightText(_cur?.name)}
                            </Typography>
                        }
						secondary={
							<React.Fragment>
                                {!(hideValue === true) && (
                                    <Typography variant="body2" color="textSecondary" noWrap style={{ display: 'block', marginTop: 2 }}>
                                        {target == 'true' && tag == 'true' ? strvalue : highlightText(_cur?.value)}
                                    </Typography>
                                )}
								{_cur?.address && (
                                    <Typography variant="caption" color="textSecondary" noWrap style={{ display: 'block', marginTop: 4 }}>
                                        {highlightText(_cur.address)}
                                    </Typography>
                                )}
							</React.Fragment>
						}/>
				</ListItem>
			);
		} else {
			return (
				null
			);
		}
	}

	const onChangeSearchText = props => event => {
		var value = event.target.value;
		// value = sanitize_regx(value);
		var text = JString(value);
		let input = text.str.toLowerCase().replace(/[\[\]()]/g, '').replace(/\s+/g, ' ').trim();
		var final = [];
		if (text.isValid()) {
			const keywords = input.split(/\s+/);
			const scored = [];
			for (let idx = 0; idx < data.length; idx++) {
				const row = data[idx];
				const {value, name, address} = row;
				const nameStr = name ? name.toLowerCase().replace(/[\[\]()]/g, '').replace(/\s+/g, ' ').trim() : '';
				const addressStr = address ? address.toLowerCase().replace(/[\[\]()]/g, '').replace(/\s+/g, ' ').trim() : '';
				let searchString = '';
				if (value) { searchString += value.trim(); }
				searchString += ' ';
				searchString += nameStr;
				searchString += ' ';
				searchString += addressStr;

				let isFound = true;
				for (const keyword of keywords) {
					if (keyword && !searchString.includes(keyword)) {
						isFound = false;
						break;
					}
				}

				if (!isFound) { continue; }

				// Relevance score: full name match > name > address, whole-word > substring
				let score = 0;
				if (nameStr === input) { score += 100000; }
				else if (nameStr.startsWith(input)) { score += 50000; }
				else if (nameStr.includes(input)) { score += 20000; }
				keywords.forEach((keyword, kwIdx) => {
					if (!keyword) { return; }
					const safe = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
					const wordRe = new RegExp('\\b' + safe + '\\b');
					if (wordRe.test(nameStr)) {
						score += 1000;
						if (kwIdx === 0 && nameStr.startsWith(keyword)) { score += 5000; }
					} else if (nameStr.includes(keyword)) {
						score += 300;
					}
					if (wordRe.test(addressStr)) {
						score += 50;
					} else if (addressStr.includes(keyword)) {
						score += 10;
					}
				});
				scored.push({row, score, idx});
			}
			scored.sort((a, b) => b.score - a.score || a.idx - b.idx);
			for (let i = 0; i < scored.length; i++) {
				final.push(scored[i].row);
			}
			setKeywords(keywords);
			setCustList(final);
		} else {
			setKeywords([]);
			setCustList(data);
		}
		setSelected(empty);setSelectedIndex(null);
	}

	return (
		<React.Fragment>
			<Dialog
				fullScreen={false}
				maxWidth={'lg'}
                fullWidth={true}
				open={open}
				onClose={() => {}}
				aria-labelledby="max-width-dialog-title"
                PaperProps={{
                    style: { borderRadius: 12 } // Modern rounded corners
                }}
            >
				<DialogTitle id="max-width-dialog-title" className={classes.dialogTitle}>
                    <div className={classes.headerContent}>
                        <Typography variant="h6" component="div" style={{ fontWeight: 600 }}>
                            {title}
                        </Typography>
                        {(description || second_description) && (
                            <div style={{ marginBottom: 8 }}>
                                {description && <Typography className={classes.subtitle}>Outlet Name: {description}</Typography>}
                                {second_description && <Typography className={classes.subtitle}>Outlet Address: {second_description}</Typography>}
                            </div>
                        )}
                    </div>
					<div className={classes.viewInRow}>
						<div className={searchClass.search}>
							<div className={searchClass.searchIcon}>
								<SearchIcon/>
							</div>
							<InputBase
								placeholder={language.search || "Search..."}
								classes={{
									root: searchClass.inputRoot,
									input: searchClass.inputInput,
								}}
								inputProps={{'aria-label': 'search'}}
								onChange={onChangeSearchText('search')}
							/>
						</div>
					</div>
				</DialogTitle>
                <Divider />
				<DialogContent style={{ padding: 0 }} className={searchClass.scrollContainer}> {/* Apply custom scrollbar style */}
                    {
                        custList.length > 0 ? (
                            <FixedSizeList
                                ref={listRef}
                                height={500} // Slightly reduced to fit better on smaller screens
                                width={'100%'}
                                itemSize={96} // Comfortable touch target size
                                itemCount={custList ? custList.length : 0}
                                outerElementType={({ style, ...rest }) => (
                                    <div
                                      style={{
                                        ...style,
                                        scrollbarColor: `${searchClass.scrollContainer.scrollbarColor}`, // Firefox support
                                      }}
                                      className={searchClass.scrollContainer} // Apply scrollbar class
                                      {...rest}
                                    />
                                )}
                            >
                                {renderRow}
                            </FixedSizeList>
                        ) : (
                            <div style={{ padding: 24, textAlign: 'center' }}>
                                <Typography color="textSecondary">No results found.</Typography>
                            </div>
                        )
                    }
				</DialogContent>
                <Divider />
				<DialogActions style={{ padding: 16 }}>
					<Button
						onClick={() => {
                            setSelected(empty);setSelectedIndex(null);
							onCreate();
						}}
                        color="primary"
                        style={{ marginRight: 'auto' }} // Push to left
						size="medium">
						Create New
					</Button>

					<Button
						onClick={() => {setSelected(empty);setSelectedIndex(null);onCancel()}}
                        color="default"
						size="medium">
						{language.cancel}
					</Button>
                    <Button
                        disabled={!selected}
						onClick={() => {
							onConfirm(selected);
							setSelectedIndex(null);
						}}
                        variant="contained" // Prominent primary action
                        color="primary"
						size="medium"
                        disableElevation
                    >
						{language.confirm}
					</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
}
